import { useEffect, useState } from 'react'
import Drawer from './Drawer'
import ConfirmDialog from './ConfirmDialog'
import RichTextEditor from './RichTextEditor'
import type { MailPreview, MailOverrides } from '../api'
import { checkWithin } from '../forms/checkForm'
import { fill, COMPONENTS_SEND_MAIL_DRAWER_TEXTS as T } from '../../shared/texts'

// The send drawer (operator, 01.10.2026): before a mail goes out, show what it says — filled
// from the templates by the SERVER (one source with the sending) — and let subject and text be
// changed for THIS mail only. The templates stay as they are. Used by the single send in the
// invoice form and by „Ausgewählte versenden" in the list.
//
// An invoice and a credit note in one selection are two mails (own wording each), so the drawer
// shows one block per mail. Only what was changed is sent back; the rest stays the template.

interface Edit { subject: string, html: string, htmlTouched: boolean }

export default function SendMailDrawer({ open, title, load, onSend, onClose }: {
    open: boolean
    title: string
    load: () => Promise<MailPreview[]>
    onSend: (overrides: MailOverrides) => Promise<void>
    onClose: () => void
}) {
    const [previews, setPreviews] = useState<MailPreview[] | null>(null)
    const [edits, setEdits] = useState<Edit[]>([])
    const [error, setError] = useState('')
    const [busy, setBusy] = useState(false)
    const [askDiscard, setAskDiscard] = useState(false)

    useEffect(() => {
        if (!open) return
        setPreviews(null); setError(''); setAskDiscard(false)
        load().then((p) => { setPreviews(p); setEdits(p.map((m) => ({ subject: m.subject, html: m.html, htmlTouched: false }))) })
            .catch((e: any) => setError(e?.response?.data?.error ?? e?.message ?? T.previewFailed))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    const changed = (i: number) => !!previews && (edits[i].subject !== previews[i].subject || edits[i].htmlTouched)
    const dirty = !!previews && previews.some((_, i) => changed(i))
    const blocked = !!previews && previews.some((p) => p.testMailBlocked || !!p.fromBlocked)
    const set = (i: number, patch: Partial<Edit>) => setEdits((prev) => prev.map((e, j) => (j === i ? { ...e, ...patch } : e)))

    const close = () => { if (busy) return; if (dirty) setAskDiscard(true); else onClose() }

    async function send(e?: { currentTarget: EventTarget | null }) {
        if (!checkWithin(e, '.drawer')) return
        if (!previews) return
        const overrides: MailOverrides = {}
        previews.forEach((p, i) => {
            if (!changed(i)) return
            overrides[p.kind] = {
                ...(edits[i].subject !== p.subject ? { subject: edits[i].subject } : {}),
                ...(edits[i].htmlTouched ? { html: edits[i].html } : {}),
            }
        })
        setBusy(true); setError('')
        try { await onSend(overrides) } catch (e: any) { setError(e?.response?.data?.error ?? e?.message ?? T.sendFailed) } finally { setBusy(false) }
    }

    return (
        <>
            <Drawer open={open} title={title} width={640} onClose={close}
                footer={<>
                    <button type="button" onClick={(e) => void send(e)} disabled={busy || !previews || blocked}>
                        {busy ? '…' : T.send}
                    </button>
                    <button type="button" className="secondary" onClick={close} disabled={busy}>{T.cancel}</button>
                </>}>
                {error && <div className="notice err">{error}</div>}
                {!previews && !error && <p className="muted">{T.loading}</p>}
                {previews?.map((p, i) => (
                    <div key={p.kind} className="panel" style={{ marginBottom: 12 }}>
                        {previews.length > 1 && <h3 style={{ marginTop: 0 }}>{p.kind === 'creditNote' ? T.mailCreditNotes : T.mailInvoices}</h3>}
                        <div className="field"><label>{T.to}</label>
                            <div>{p.to}{p.testMailTo && <> — <b>{fill(T.testMailTo, { to: p.testMailTo })}</b></>}</div>
                            {p.testMailBlocked && <div className="notice err" style={{ marginTop: 6 }}>{T.testMailBlocked}</div>}
                        </div>
                        <div className="field"><label>{T.from}</label>
                            <div>{p.from || '—'}</div>
                            {p.fromBlocked && <div className="notice err" style={{ marginTop: 6 }}>{p.fromBlocked}</div>}
                        </div>
                        <div className="field"><label>{T.attachments}</label>
                            <div>{p.invoiceNumbers.join(', ')}</div>
                            {p.alreadySent.length > 0 && <small className="muted">{fill(T.alreadySent, { numbers: p.alreadySent.join(', ') })}</small>}
                        </div>
                        <div className="field"><label>{T.subject}</label>
                            <input type="text" value={edits[i]?.subject ?? ''} required onChange={(e) => set(i, { subject: e.target.value })} /></div>
                        <div className="field"><label>{T.text} <span className="hint">{T.textHint}</span></label>
                            <RichTextEditor value={edits[i]?.html ?? ''} onChange={(html) => set(i, { html, htmlTouched: true })} /></div>
                    </div>
                ))}
            </Drawer>
            <ConfirmDialog
                open={askDiscard}
                title={T.discardTitle}
                message={T.discardMessage}
                confirmLabel={T.discard}
                danger
                onConfirm={() => { setAskDiscard(false); onClose() }}
                onCancel={() => setAskDiscard(false)}
            />
        </>
    )
}
